/**
 * Union types and aggregate type guards for blocks.
 *
 * This file provides the discriminated union of all block types
 * and helper type guards for common block categories.
 */

// Canvas blocks
import type { PlayBlock } from './canvas/Play'
import type { FormationBlock } from './canvas/Formation'
import type { ConceptBlock } from './canvas/Concept'
import type { PlayerBlock } from './canvas/Player'
import type { DrawingBlock } from './canvas/Drawing'
import type { ControlPointBlock } from './canvas/ControlPoint'
import type { AnnotationBlock } from './canvas/Annotation'
import type { PreSnapMarkerBlock } from '@/types/blocks/canvas'

// Document blocks
import type { DocumentBlock } from './document/Document'
import type { SectionBlock } from './document/Section'
import type { TextBlock } from './document/Text'
import type { HeaderBlock } from './document/Header'
import type { DividerBlock } from './document/Divider'
import type { ImageBlock } from './document/Image'
import type { ShapeBlock } from './document/Shape'

// Reference blocks
import type { ConceptReferenceBlock } from '@/types/blocks/reference'
import type { PlayReferenceBlock } from '@/types/blocks/reference'
import type { FormationReferenceBlock } from '@/types/blocks/reference'
import type { DocumentReferenceBlock } from '@/types/blocks/reference'

// Layout blocks
import type { RowBlock } from './layout/Row'
import type { ColumnBlock } from './layout/Column'

// Enums
import {
	ROOT_BLOCK_TYPES,
	CANVAS_ROOT_TYPES,
	DOCUMENT_ROOT_TYPES,
	CANVAS_CONTENT_TYPES,
	DOCUMENT_CONTENT_TYPES,
	LAYOUT_CONTAINER_TYPES,
	REFERENCE_BLOCK_TYPES,
} from './Enums'

/**
 * Discriminated union of all block types.
 * Use block.type to narrow the type.
 *
 * Removed types (not in this union):
 * - BezierHandleBlock: handles now embedded in ControlPointBlock.properties.handles
 * - PathSegmentBlock: replaced by block_references with reference_type='path_connection'
 */
export type PlayBlock_Union =
	// Canvas root blocks
	| PlayBlock
	| FormationBlock
	| ConceptBlock
	// Canvas content blocks
	| PlayerBlock
	| DrawingBlock
	| ControlPointBlock
	| AnnotationBlock
	| PreSnapMarkerBlock
	| ConceptReferenceBlock
	// Document root blocks
	| DocumentBlock
	// Document content blocks
	| SectionBlock
	| TextBlock
	| HeaderBlock
	| DividerBlock
	| ImageBlock
	| ShapeBlock
	// Reference blocks
	| PlayReferenceBlock
	| FormationReferenceBlock
	| DocumentReferenceBlock
	// Layout blocks
	| RowBlock
	| ColumnBlock

/**
 * Union of all root-level blocks (blocks that can exist at root level).
 */
export type RootBlock =
	| PlayBlock
	| FormationBlock
	| ConceptBlock
	| DocumentBlock

/**
 * Union of canvas root blocks.
 */
export type CanvasRootBlock =
	| PlayBlock
	| FormationBlock
	| ConceptBlock

/**
 * Union of document root blocks.
 */
export type DocumentRootBlock =
	| DocumentBlock

/**
 * Union of layout container blocks.
 */
export type LayoutContainer =
	| PlayBlock
	| FormationBlock
	| ConceptBlock
	| DocumentBlock
	| SectionBlock
	| RowBlock
	| ColumnBlock

/**
 * Union of canvas content blocks (children of plays/formations/concepts).
 */
export type CanvasContentBlock =
	| PlayerBlock
	| DrawingBlock
	| ControlPointBlock
	| AnnotationBlock
	| PreSnapMarkerBlock

/**
 * Union of document content blocks (children of documents/presentations).
 */
export type DocumentContentBlock =
	| SectionBlock
	| TextBlock
	| HeaderBlock
	| DividerBlock
	| ShapeBlock
	| ImageBlock

/**
 * Union of reference blocks.
 */
export type ReferenceBlock =
	| ConceptReferenceBlock
	| PlayReferenceBlock
	| FormationReferenceBlock
	| DocumentReferenceBlock

// ========== Category Type Guards ==========

/**
 * Type guard for root blocks (can exist at root level).
 */
export function isRootBlock(block: PlayBlock_Union): block is RootBlock {
	return ROOT_BLOCK_TYPES.includes(block.type)
}

/**
 * Type guard for canvas root blocks.
 */
export function isCanvasRootBlock(block: PlayBlock_Union): block is CanvasRootBlock {
	return CANVAS_ROOT_TYPES.includes(block.type)
}

/**
 * Type guard for document root blocks.
 */
export function isDocumentRootBlock(block: PlayBlock_Union): block is DocumentRootBlock {
	return DOCUMENT_ROOT_TYPES.includes(block.type)
}

/**
 * Type guard for layout container blocks.
 */
export function isLayoutContainer(block: PlayBlock_Union): block is LayoutContainer {
	return LAYOUT_CONTAINER_TYPES.includes(block.type)
}

/**
 * Type guard for canvas content blocks (children of plays/formations/concepts).
 */
export function isCanvasContentBlock(block: PlayBlock_Union): block is CanvasContentBlock {
	return CANVAS_CONTENT_TYPES.includes(block.type)
}

/**
 * Type guard for document content blocks (children of documents/presentations).
 */
export function isDocumentContentBlock(block: PlayBlock_Union): block is DocumentContentBlock {
	return DOCUMENT_CONTENT_TYPES.includes(block.type)
}

/**
 * Type guard for reference blocks.
 */
export function isReferenceBlock(block: PlayBlock_Union): block is ReferenceBlock {
	return REFERENCE_BLOCK_TYPES.includes(block.type)
}
